import { useState } from "react";
import { rfqApi } from "@/lib/api-client";
import { toast } from "sonner";
import { ShieldCheck, Scale, Archive, Loader2, ChevronDown, Lock, Landmark } from "lucide-react";

const STATUS_CLS = {
  pending: "border-amber-500/50 text-amber-400",
  authorized: "border-sky-500/50 text-sky-300",
  executed: "border-emerald-500/50 text-emerald-400",
  failed: "border-rose-500/50 text-rose-400",
  rejected: "border-white/20 text-white/40",
};
const ESCROW_CLS = {
  held: "border-amber-500/40 text-amber-400",
  settled: "border-emerald-500/40 text-emerald-400",
  released: "border-rose-500/40 text-rose-300",
};

export const SovereignRFQCard = ({ rfq: initial, onArbitrageAuthorized, onExecuted }) => {
  const [rfq, setRfq] = useState(initial);
  const [busy, setBusy] = useState(null);
  const [negOpen, setNegOpen] = useState(false);
  const [counter, setCounter] = useState("");
  const [maxCr, setMaxCr] = useState("");
  const [exec, setExec] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);

  const refresh = async () => { try { setRfq(await rfqApi.get(rfq.id)); } catch {} };

  const authorize = async () => {
    setBusy("authorize");
    toast.info("AUTHORIZED — escrow held, APPROVED_RFQ token granted. Executing…");
    try {
      const r = await rfqApi.authorize(rfq.id);
      if (r.execute_via === "arbitrage_approve") {
        setRfq(r.rfq);
        await onArbitrageAuthorized?.(r);
        await refresh();
      } else {
        setRfq(r.rfq);
        setExec({ reply: r.reply, actions: r.actions, proof_links: r.proof_links || [] });
        onExecuted?.(r);
        const ok = (r.actions || []).some((a) => a.ok);
        ok ? toast.success("Proof of Work delivered — escrow settled")
           : toast.warning("No Proof of Work — escrow released back to your pool");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Authorization failed");
      await refresh();
    } finally { setBusy(null); }
  };

  const negotiate = async () => {
    setBusy("negotiate");
    try {
      const updated = await rfqApi.negotiate(rfq.id, counter, maxCr ? Number(maxCr) : undefined);
      setRfq(updated);
      setNegOpen(false);
      toast.success(`Counter-offer bound — revised Direct Cost ${updated.direct_cost_credits}cr`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Negotiation failed");
    } finally { setBusy(null); }
  };

  const reject = async () => {
    setBusy("reject");
    try {
      await rfqApi.reject(rfq.id);
      setRfq((x) => ({ ...x, status: "rejected" }));
      toast.info("RFQ rejected — opportunity archived. Nothing executed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rejection failed");
    } finally { setBusy(null); }
  };

  const pending = rfq.status === "pending";
  const pow = rfq.proof_of_work || [];
  return (
    <div className="mt-2 border border-amber-500/40 bg-black/60 p-3" data-testid="rfq-card">
      <div className="flex items-center gap-2 flex-wrap">
        <Lock size={11} className="text-amber-400 flex-none" />
        <span className="font-mono-uc text-[9px] uppercase tracking-widest text-amber-400">Sovereign RFQ</span>
        <span data-testid="rfq-status"
              className={`font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border ${STATUS_CLS[rfq.status] || STATUS_CLS.pending}`}>
          {rfq.status}
        </span>
        {rfq.escrow?.status && (
          <span data-testid="rfq-escrow-badge"
                className={`font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border flex items-center gap-1 ${ESCROW_CLS[rfq.escrow.status]}`}>
            <Landmark size={8} /> Escrow {rfq.escrow.status} · {rfq.escrow.held_credits}cr
          </span>
        )}
        <span className="ml-auto font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-white/15 text-white/60"
              data-testid="rfq-venture">{rfq.venture}</span>
      </div>
      <div className="text-[12px] text-white/90 mt-2" data-testid="rfq-title">{rfq.title}</div>
      {rfq.rationale && <div className="text-[10px] text-white/50 mt-0.5">{rfq.rationale}</div>}
      {/* Clinical Breakdown */}
      <div className="grid grid-cols-2 gap-2 mt-2">
        <div className="border border-white/10 px-2 py-1.5">
          <div className="font-mono-uc text-[8px] uppercase tracking-widest text-white/40">Direct Cost</div>
          <div className="font-mono-uc text-[13px] text-rose-300" data-testid="rfq-direct-cost">{rfq.direct_cost_credits}cr</div>
        </div>
        <div className="border border-white/10 px-2 py-1.5">
          <div className="font-mono-uc text-[8px] uppercase tracking-widest text-white/40">Projected ROI</div>
          <div className="font-mono-uc text-[13px] text-emerald-400" data-testid="rfq-projected-roi">
            ${Number(rfq.projected_roi_usd || 0).toFixed(2)}
          </div>
        </div>
      </div>
      <div className="font-mono-uc text-[9px] text-white/45 mt-1.5" data-testid="rfq-tools">
        Plan: {(rfq.tools_planned || []).map((t) => t.tool).join(" → ") || "—"}
      </div>
      {rfq.counter_offer && (
        <div className="font-mono-uc text-[9px] text-sky-300 mt-1" data-testid="rfq-counter-note">
          Counter-offer bound: {rfq.counter_offer.text || "budget cap"}{rfq.counter_offer.max_credits ? ` · ≤${rfq.counter_offer.max_credits}cr` : ""}
        </div>
      )}
      {pending && (
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <button data-testid="rfq-authorize-btn" onClick={authorize} disabled={!!busy}
                  className="uc-btn-amber px-3 py-1.5 font-mono-uc text-[9px] uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50">
            {busy === "authorize" ? <Loader2 size={10} className="animate-spin" /> : <ShieldCheck size={10} />} Authorize
          </button>
          <button data-testid="rfq-negotiate-btn" onClick={() => setNegOpen((o) => !o)} disabled={!!busy}
                  className="px-3 py-1.5 border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 font-mono-uc text-[9px] uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50">
            <Scale size={10} /> Negotiate
          </button>
          <button data-testid="rfq-reject-btn" onClick={reject} disabled={!!busy}
                  className="px-3 py-1.5 border border-white/15 text-white/50 hover:border-rose-500/40 hover:text-rose-300 font-mono-uc text-[9px] uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50">
            {busy === "reject" ? <Loader2 size={10} className="animate-spin" /> : <Archive size={10} />} Reject
          </button>
        </div>
      )}
      {pending && negOpen && (
        <div className="mt-2 border border-sky-500/25 bg-sky-500/[0.04] p-2 space-y-2" data-testid="rfq-negotiate-panel">
          <textarea data-testid="rfq-counter-input" value={counter} onChange={(e) => setCounter(e.target.value)} rows={2}
                    placeholder="Counter-offer — limit the scope (e.g. 'skip the payout, research only')…"
                    className="w-full bg-black border border-white/15 px-2 py-1.5 text-[11px] text-white/90 focus:border-sky-500/50 outline-none resize-none" />
          <div className="flex items-center gap-2">
            <input data-testid="rfq-max-credits-input" value={maxCr} onChange={(e) => setMaxCr(e.target.value.replace(/[^0-9.]/g, ""))}
                   placeholder="Credit cap"
                   className="w-28 bg-black border border-white/15 px-2 py-1.5 text-[11px] text-white/90 focus:border-sky-500/50 outline-none" />
            <button data-testid="rfq-counter-submit-btn" onClick={negotiate} disabled={busy === "negotiate"}
                    className="px-3 py-1.5 border border-sky-500/50 text-sky-300 hover:bg-sky-500/10 font-mono-uc text-[9px] uppercase tracking-wider disabled:opacity-50">
              {busy === "negotiate" ? <Loader2 size={10} className="animate-spin" /> : "Bind Counter-Offer"}
            </button>
          </div>
        </div>
      )}
      {exec?.reply && (
        <div className="mt-2 border border-emerald-500/25 bg-emerald-500/[0.03] p-2 text-[11px] text-white/80 whitespace-pre-wrap"
             data-testid="rfq-exec-verdict">{exec.reply}</div>
      )}
      {(exec?.proof_links || []).length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="rfq-proof-links">
          {exec.proof_links.map((l, i) => (
            <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${l}`} target="_blank" rel="noreferrer"
               data-testid={`rfq-proof-link-${i}`}
               className="font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
              Proof of Work ↓
            </a>
          ))}
        </div>
      )}
      {(pow.length > 0 || ["authorized", "executed", "failed"].includes(rfq.status)) && (
        <div className="mt-2">
          <button data-testid="rfq-audit-toggle" onClick={async () => { if (!auditOpen) await refresh(); setAuditOpen((o) => !o); }}
                  className="font-mono-uc text-[8px] uppercase tracking-widest text-white/40 hover:text-white/70 flex items-center gap-1">
            Live Audit · Proof of Work ({pow.length}) <ChevronDown size={9} className={auditOpen ? "rotate-180" : ""} />
          </button>
          {auditOpen && (
            <div className="mt-1 border border-white/10 bg-black max-h-40 overflow-y-auto uc-scroll" data-testid="rfq-audit-log">
              {(rfq.proof_of_work || []).map((s) => (
                <div key={s.id} className="px-2 py-1 border-b border-white/5 last:border-none">
                  <span className="font-mono-uc text-[9px] text-emerald-400/80">{s.step}</span>
                  {s.detail && <span className="font-mono-uc text-[8px] text-white/35 ml-2">{s.detail}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
